import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { adminService } from '@/lib/api/services/admin.service'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Eye, Users, BarChart3, Globe, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'

const RANGE_OPTIONS = [
  { label: '3d', days: 3 },
  { label: '7d', days: 7 },
  { label: '10d', days: 10 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
] as const

function getDateRange(days: number) {
  const end = new Date().toISOString().split('T')[0]
  const start = new Date(Date.now() - days * 86400000).toISOString().split('T')[0]
  return { start, end }
}

function StatCard({
  icon: Icon,
  value,
  label,
  tooltip,
  iconBgColor,
  iconColor,
}: {
  icon: React.ComponentType<{ className?: string }>
  value: string | number
  label: string
  tooltip: string
  iconBgColor: string
  iconColor: string
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="bg-white rounded-3xl border border-slate-200 p-6 flex flex-col items-center">
            <div className={cn('w-14 h-14 rounded-full flex items-center justify-center mb-3', iconBgColor)}>
              <Icon className={cn('w-6 h-6', iconColor)} />
            </div>
            <div className="text-4xl font-semibold text-slate-900">{value}</div>
            <div className="text-sm text-slate-500 mt-2">{label}</div>
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="text-xs">{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export function SiteAnalytics() {
  const [selectedDays, setSelectedDays] = useState(30)
  const { start, end } = getDateRange(selectedDays)

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['site-overview', start, end],
    queryFn: () => adminService.getSiteOverview(start, end),
    staleTime: 5 * 60 * 1000,
  })

  const { data: trends = [], isLoading: trendsLoading } = useQuery({
    queryKey: ['site-trends', start, end],
    queryFn: () => adminService.getSiteTrends(start, end),
    staleTime: 5 * 60 * 1000,
  })

  const { data: topPages = [], isLoading: pagesLoading } = useQuery({
    queryKey: ['site-top-pages', start, end],
    queryFn: () => adminService.getSiteTopPages(start, end, 20),
    staleTime: 5 * 60 * 1000,
  })

  const { data: referrers = [], isLoading: referrersLoading } = useQuery({
    queryKey: ['site-referrers', start, end],
    queryFn: () => adminService.getSiteReferrers(start, end, 20),
    staleTime: 5 * 60 * 1000,
  })

  return (
    <div className="space-y-8">
      {/* Quick Range Selector */}
      <div className="flex items-center gap-2">
        {RANGE_OPTIONS.map(({ label, days }) => (
          <button
            key={days}
            onClick={() => setSelectedDays(days)}
            className={cn(
              'px-4 py-2 rounded-full text-sm font-medium transition-colors',
              selectedDays === days
                ? 'bg-blue-600 text-white'
                : 'bg-white border border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-600'
            )}
          >
            {label}
          </button>
        ))}
        <span className="text-sm text-slate-400 ml-2">
          {start} to {end}
        </span>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {overviewLoading ? (
          [1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-3xl border border-slate-200 p-6 flex flex-col items-center animate-pulse">
              <div className="w-14 h-14 rounded-full bg-slate-100 mb-3" />
              <div className="h-10 w-20 bg-slate-100 rounded mb-2" />
              <div className="h-4 w-24 bg-slate-100 rounded" />
            </div>
          ))
        ) : (
          <>
            <StatCard
              icon={Eye}
              value={overview?.pageviews?.toLocaleString() || '0'}
              label="Page Views"
              tooltip="Total page views in the selected period"
              iconBgColor="bg-[rgba(59,130,246,0.10)]"
              iconColor="text-blue-600"
            />
            <StatCard
              icon={Users}
              value={overview?.uniqueVisitors?.toLocaleString() || '0'}
              label="Unique Visitors"
              tooltip="Distinct users who visited the site"
              iconBgColor="bg-[rgba(34,197,94,0.10)]"
              iconColor="text-green-600"
            />
            <StatCard
              icon={BarChart3}
              value={overview?.sessions?.toLocaleString() || '0'}
              label="Sessions"
              tooltip="Total browsing sessions"
              iconBgColor="bg-[rgba(168,85,247,0.10)]"
              iconColor="text-purple-600"
            />
          </>
        )}
      </div>

      {/* Trends Chart */}
      {trendsLoading && (
        <div className="bg-white rounded-3xl border border-slate-200 p-6">
          <div className="h-5 w-48 bg-slate-100 rounded mb-4 animate-pulse" />
          <div className="h-[300px] bg-slate-50 rounded animate-pulse" />
        </div>
      )}
      {!trendsLoading && trends.length > 0 && (
        <div className="bg-white rounded-3xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4">Traffic Over Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trends} margin={{ left: 20, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <RechartsTooltip
                labelFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              />
              <Legend />
              <Line type="monotone" dataKey="pageviews" stroke="#3b82f6" strokeWidth={2} dot={false} name="Page Views" />
              <Line type="monotone" dataKey="visitors" stroke="#22c55e" strokeWidth={2} dot={false} name="Visitors" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Top Pages and Referrers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-3xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4">Top Pages</h3>
          {pagesLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : topPages.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Page</TableHead>
                    <TableHead className="text-right">Views</TableHead>
                    <TableHead className="text-right">Visitors</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topPages.map((p, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium text-sm max-w-[250px] truncate">{p.page}</TableCell>
                      <TableCell className="text-right text-sm">{p.views}</TableCell>
                      <TableCell className="text-right text-sm">{p.uniqueVisitors}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-slate-400 text-sm text-center py-8">No page data available yet</p>
          )}
        </div>

        <div className="bg-white rounded-3xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="h-5 w-5 text-slate-400" />
            <h3 className="font-semibold text-slate-900">Top Referrers</h3>
          </div>
          {referrersLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : referrers.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Source</TableHead>
                    <TableHead className="text-right">Visits</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referrers.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium text-sm max-w-[300px] truncate">{r.referrer}</TableCell>
                      <TableCell className="text-right text-sm">{r.visits}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-slate-400 text-sm text-center py-8">No referrer data available yet</p>
          )}
        </div>
      </div>
    </div>
  )
}
